import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react"
import { HEADER_HEIGHT } from "@/constants/ui"
import useScrollspy from "@/hooks/use-scrollspy"
import { cn } from "cn"

import { scrollToActiveItemX } from "@/lib/utils/dom"

// Every ScrollNavContent with a value renders this data-slot and an `id`; the
// scrollspy discovers sections by querying for it rather than a ref map.
const SECTION_SELECTOR = '[data-slot="scroll-nav-content"][id]'

const ScrollNavContext = createContext<{
  activeSection: string | undefined
  navHeight: number
  navOffset: number
  scrollingElement?: RefObject<HTMLElement | null>
  scrollPadding: number
  sections: Set<string>
  registerSection: (value: string) => void
} | null>(null)

interface ScrollNavProps {
  navHeight?: number
  navOffset?: number
  // Called with the active section's DOM id whenever it changes. Callers own
  // the mapping from id to whatever they need (e.g. an analytics section name).
  onSectionChange?: (activeSectionId: string) => void
  scrollingElement?: RefObject<HTMLElement | null>
  children: ReactNode
}

export const ScrollNav: FC<ScrollNavProps> = ({
  children,
  navHeight = HEADER_HEIGHT,
  navOffset = 0,
  onSectionChange,
  scrollingElement,
}) => {
  const scrollPadding = navHeight + navOffset

  const activeSection = useScrollspy({
    sectionSelector: SECTION_SELECTOR,
    scrollingElement,
    offsetPx: scrollPadding,
  })

  useEffect(() => {
    const currentScrollingElement = scrollingElement?.current

    if (currentScrollingElement) {
      currentScrollingElement.style.scrollPaddingTop = `${scrollPadding - 1}px`
      currentScrollingElement.style.setProperty(
        "--scroll-nav-min-height",
        `${currentScrollingElement.clientHeight - scrollPadding + 1}px`
      )
    } else {
      document.documentElement.style.scrollPaddingTop = `${scrollPadding - 1}px`
      document.documentElement.style.setProperty(
        "--scroll-nav-min-height",
        `${window.innerHeight - scrollPadding + 1}px`
      )
    }

    const handleResize = () => {
      if (!currentScrollingElement) {
        document.documentElement.style.setProperty(
          "--scroll-nav-min-height",
          `${window.innerHeight - scrollPadding + 1}px`
        )
      }
    }

    if (!currentScrollingElement) {
      window.addEventListener("resize", handleResize)
    }

    return () => {
      if (!currentScrollingElement) {
        window.removeEventListener("resize", handleResize)
        document.documentElement.style.removeProperty("scroll-padding-top")
        document.documentElement.style.removeProperty("--scroll-nav-min-height")
      }
    }
  }, [scrollingElement, scrollPadding])

  useEffect(() => {
    if (activeSection) onSectionChange?.(activeSection)
  }, [activeSection, onSectionChange])

  const [sections, setSections] = useState(new Set<string>())

  const registerSection = (value: string) => {
    setSections((prev) => (prev.has(value) ? prev : new Set(prev).add(value)))
  }

  return (
    <ScrollNavContext.Provider
      value={{
        activeSection,
        navHeight,
        navOffset,
        scrollingElement,
        scrollPadding,
        sections,
        registerSection,
      }}
    >
      <>{children}</>
    </ScrollNavContext.Provider>
  )
}

export const ScrollNavList: FC<
  HTMLAttributes<HTMLUListElement> & {
    sheet?: boolean
  }
> = ({ children, sheet, ...props }) => {
  const context = useContext(ScrollNavContext)

  if (!context) {
    throw new Error("ScrollNavList must be used within a ScrollNav")
  }

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const { navHeight, navOffset, activeSection } = context

  useEffect(() => {
    if (scrollRef.current && activeSection) {
      scrollToActiveItemX(scrollRef, `[data-scroll-to="${activeSection}"]`)
    }
  }, [activeSection])

  return (
    <div
      className={cn(
        "sticky z-1 overflow-hidden bg-background",
        sheet ? "-mx-(--sheet-pad)" : "-mx-(--container-pad)"
      )}
      style={{
        height: navHeight,
        top: navOffset,
      }}
    >
      <div
        ref={scrollRef}
        className="scrollbar-hidden flex h-full overflow-x-scroll scroll-smooth"
      >
        <div
          className={cn(
            "shrink-0",
            sheet ? "w-(--sheet-pad)" : "w-(--container-pad)"
          )}
        />
        <ul
          className="flex h-full shrink-0 grow items-end gap-1.5 border-b font-medium *:-mb-px sm:gap-2.5"
          {...props}
        >
          {children}
        </ul>
        <div
          className={cn(
            "shrink-0",
            sheet ? "w-(--sheet-pad)" : "w-(--container-pad)"
          )}
        />
      </div>
    </div>
  )
}

export const ScrollNavItem: FC<HTMLAttributes<HTMLLIElement>> = ({
  children,
  ...props
}) => <li {...props}>{children}</li>

interface ScrollNavAnchorProps extends HTMLAttributes<HTMLButtonElement> {
  value: string
}

export const ScrollNavAnchor: FC<ScrollNavAnchorProps> = ({
  value,
  className,
  children,
  onClick,
  ...props
}) => {
  const context = useContext(ScrollNavContext)
  if (!context) {
    throw new Error("ScrollNavList must be used within a ScrollNav")
  }

  useEffect(() => {
    context.registerSection(value)
  }, [value, context])

  const { activeSection, scrollingElement, scrollPadding } = context

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    const element = document.getElementById(value)
    if (element) {
      // ios web view scrolls relative to the page and not to a scrolling element, because of this we need to adjust the
      // scroll position to take into account the header height. This is triggered when scrollingElement is null (ios
      // web view) and pageScrollOffset is set.
      if (scrollingElement?.current === null) {
        const margin = scrollPadding - 1
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY

        // ios web view can go negative due to native content, don't let it go past 0
        const offsetPosition = Math.max(elementPosition - margin, 0)

        window.scrollTo({
          top: offsetPosition,
        })

        setTimeout(() => {
          if (activeSection !== value) {
            // ios web view can go negative due to native content, don't let it go past 0
            const newPosition = Math.max(
              element.getBoundingClientRect().top + window.scrollY - margin,
              0
            )

            window.scrollTo({
              top: newPosition,
            })
          }
          element.focus()
        }, 100)
      } else {
        element.scrollIntoView()
        setTimeout(() => {
          if (activeSection !== value) {
            element.scrollIntoView()
          }
          element.focus()
        }, 100)
      }
    }
  }

  return (
    <button
      type="button"
      data-scroll-to={value}
      onClick={handleClick}
      className={cn(
        "block border-b-2 border-transparent px-2 py-1.5 text-muted hover:text-foreground",
        (activeSection === value || activeSection === `${value}-content`) &&
          "border-current text-foreground",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}

interface ScrollNavContentProps extends HTMLAttributes<HTMLDivElement> {
  value?: string
}

export function ScrollNavContent({
  ref,
  value,
  children,
  className,
  ...props
}: ScrollNavContentProps & { ref?: Ref<HTMLDivElement> }) {
  const context = useContext(ScrollNavContext)
  const isInNavList = value && context?.sections.has(value)

  return (
    <div
      ref={ref}
      id={value}
      className={cn(
        "py-9 [&+&]:border-t",
        isInNavList && "last:min-h-(--scroll-nav-min-height)",
        className
      )}
      tabIndex={-1}
      data-slot="scroll-nav-content"
      {...props}
    >
      {children}
    </div>
  )
}

ScrollNavContent.displayName = "ScrollNavContent"
